import { useEffect, useMemo, useState } from 'react';
import { ApiError, apiFetch } from '../api/client.ts';
import {
  ALL_BUCKETS,
  TICKETLESS_BILLABLE_BUCKETS,
  bucketForToggle,
  bucketLabel,
  isBillableBucket,
  type BillingBucket,
} from '../timer/buckets.ts';
import { Button } from './Button.tsx';

/**
 * A calendar event, reduced to what it takes to log it as time. Built by the
 * calendar page from a Google event, a T3 team event, or a check-in — the
 * modal doesn't care which.
 */
export interface CalendarTimeTarget {
  /** Stable per-source key; makes the log idempotent (migration 0077). */
  externalCalendarEventId: string;
  title: string;
  /** Event start, ISO 8601. Becomes the entry's started_at verbatim. */
  startUtc: string;
  /** Scheduled length; the agent can trim it to what the call actually ran. */
  durationMinutes: number;
  /** Org this event is definitively for (team events, check-ins). */
  organizationId?: string | null;
  organizationName?: string | null;
  /** Org guessed from attendees (Google events) — always shown as a guess. */
  suggestedOrganizationId?: string | null;
  suggestedOrganizationName?: string | null;
  suggestedMatchedOn?: 'contact' | 'domain' | null;
}

interface OrgOption {
  id: string;
  name: string;
}

interface Props {
  target: CalendarTimeTarget;
  /** Calendar's display timezone, so the confirmation reads like the calendar. */
  timeZone: string;
  onSaved: (info: { minutes: number; bucket: BillingBucket; orgName: string | null }) => void;
  onClose: () => void;
}

const QUICK_MINUTES = [15, 30, 45, 60, 90];

function formatWhen(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function LogCalendarTimeModal({
  target,
  timeZone,
  onSaved,
  onClose,
}: Props): React.ReactElement {
  // A meeting with a client attached is billable by default — that's the whole
  // point of logging it. With no client in sight, open on Non-billable so a
  // careless save can't invent a client charge.
  const knownOrgId = target.organizationId ?? target.suggestedOrganizationId ?? '';
  const [billable, setBillable] = useState(Boolean(knownOrgId));
  const [exactBucket, setExactBucket] = useState<BillingBucket | null>(null);
  const [showAllBuckets, setShowAllBuckets] = useState(false);
  const [organizationId, setOrganizationId] = useState<string>(knownOrgId);
  const [minutes, setMinutes] = useState<number>(target.durationMinutes);
  const [notes, setNotes] = useState<string>(target.title);
  const [orgs, setOrgs] = useState<OrgOption[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bucket: BillingBucket =
    exactBucket ?? bucketForToggle({ billable, derived: null, hasTicket: false });
  const needsOrg = TICKETLESS_BILLABLE_BUCKETS.has(bucket);

  useEffect(() => {
    apiFetch<{ organizations: OrgOption[] }>('/api/organizations')
      .then((r) => setOrgs(r.organizations.map((o) => ({ id: o.id, name: o.name }))))
      .catch(() => setOrgs([]));
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const orgName = useMemo(
    () =>
      organizationId
        ? (orgs?.find((o) => o.id === organizationId)?.name ??
          target.organizationName ??
          target.suggestedOrganizationName ??
          null)
        : null,
    [organizationId, orgs, target.organizationName, target.suggestedOrganizationName],
  );

  async function save(): Promise<void> {
    if (minutes < 1 || minutes > 24 * 60) {
      setError('Duration must be between 1 minute and 24 hours.');
      return;
    }
    if (needsOrg && !organizationId) {
      setError('Pick the client this time bills to.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/timesheets/internal', {
        method: 'POST',
        json: {
          bucket,
          startedAt: new Date(target.startUtc).toISOString(),
          durationMinutes: minutes,
          notes: notes.trim() || target.title,
          // Sent for non-billable buckets too, so a client check-in still
          // attributes to that client in per-org rollups. Omitted entirely when
          // no org is picked, which the server reads as T3-internal time.
          ...(organizationId ? { organizationId } : {}),
          externalCalendarEventId: target.externalCalendarEventId,
        },
      });
      onSaved({ minutes, bucket, orgName });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Someone (another tab, an earlier click) already logged it. Treat as
        // success so the calendar refreshes into its logged state.
        onSaved({ minutes, bucket, orgName });
        return;
      }
      setError(e instanceof ApiError ? e.message : 'Failed to log this event as time.');
    } finally {
      setBusy(false);
    }
  }

  const suggestionNote =
    !target.organizationId && target.suggestedOrganizationId && organizationId === target.suggestedOrganizationId
      ? target.suggestedMatchedOn === 'contact'
        ? `Matched from an attendee who is a contact at ${target.suggestedOrganizationName}.`
        : `Guessed from an attendee's email domain — check it.`
      : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-12"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[var(--color-header)]">Log this as time</h2>
        <p className="mt-1 truncate text-sm font-medium text-[var(--color-body)]" title={target.title}>
          {target.title}
        </p>
        <p className="text-xs text-[var(--color-muted)]">{formatWhen(target.startUtc, timeZone)}</p>

        <div className="mt-4 space-y-4">
          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
              Time type
            </span>
            <div className="flex overflow-hidden rounded-[var(--radius-button)] border border-[var(--color-border)] text-sm">
              {[
                { key: true, label: 'Billable' },
                { key: false, label: 'Non-billable' },
              ].map((side) => (
                <button
                  key={String(side.key)}
                  type="button"
                  onClick={() => {
                    setBillable(side.key);
                    setExactBucket(null);
                  }}
                  className={
                    'flex-1 px-3 py-2 transition ' +
                    (billable === side.key
                      ? 'bg-[var(--color-t3-blue)] font-semibold text-white'
                      : 'bg-white text-[var(--color-muted)] hover:bg-[var(--color-surface)]')
                  }
                >
                  {side.label}
                </button>
              ))}
            </div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="truncate text-[11px] text-[var(--color-muted)]">
                {bucketLabel(bucket)}
              </span>
              <button
                type="button"
                onClick={() => setShowAllBuckets((v) => !v)}
                className="shrink-0 text-[11px] text-[var(--color-t3-blue)] hover:underline"
              >
                {showAllBuckets ? 'less' : 'other…'}
              </button>
            </div>
            {showAllBuckets && (
              <select
                value={bucket}
                onChange={(e) => {
                  const next = e.target.value as BillingBucket;
                  setExactBucket(next);
                  setBillable(isBillableBucket(next));
                }}
                className="mt-1 block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-2 text-sm outline-none focus:border-[var(--color-t3-blue)]"
              >
                {ALL_BUCKETS.filter((b) => b.inTicketless).map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
              Client {needsOrg ? '' : '(optional)'}
            </span>
            <select
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-2 text-sm outline-none focus:border-[var(--color-t3-blue)]"
            >
              <option value="">
                {needsOrg ? '— pick a client —' : '— none / T3 internal —'}
              </option>
              {(orgs ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
              {/* Keep a pre-picked org selectable while the list is loading. */}
              {orgs === null && organizationId && (
                <option value={organizationId}>
                  {target.organizationName ?? target.suggestedOrganizationName ?? 'Loading…'}
                </option>
              )}
            </select>
            {suggestionNote && (
              <p className="mt-1 text-[11px] text-[var(--color-muted)]">{suggestionNote}</p>
            )}
          </div>

          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
              Duration
            </span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={24 * 60}
                step={5}
                value={minutes}
                onChange={(e) => setMinutes(Math.max(1, Number(e.target.value) || 0))}
                className="w-24 rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2 text-sm"
              />
              <span className="text-xs text-[var(--color-muted)]">min</span>
              <div className="ml-auto flex gap-1">
                {QUICK_MINUTES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMinutes(m)}
                    className={
                      'rounded-full border px-2 py-1 text-[11px] transition ' +
                      (minutes === m
                        ? 'border-[var(--color-t3-blue)] bg-[var(--color-t3-blue)]/10 font-semibold text-[var(--color-t3-blue)]'
                        : 'border-[var(--color-border)] text-[var(--color-muted)] hover:bg-[var(--color-surface)]')
                    }
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            {minutes !== target.durationMinutes && (
              <button
                type="button"
                onClick={() => setMinutes(target.durationMinutes)}
                className="mt-1 text-[11px] text-[var(--color-t3-blue)] hover:underline"
              >
                back to scheduled ({target.durationMinutes} min)
              </button>
            )}
          </div>

          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
              Notes
            </span>
            <textarea
              rows={2}
              value={notes}
              maxLength={2000}
              onChange={(e) => setNotes(e.target.value)}
              className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2 text-sm"
            />
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}

        <div className="mt-5 flex gap-2 border-t border-[var(--color-border)] pt-4">
          <Button onClick={() => void save()} disabled={busy}>
            {busy ? 'Logging…' : `Log ${minutes} min`}
          </Button>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
